import React, { useState, useEffect, useRef } from "react";
import {
	Modal,
	Progress,
	Space,
	Typography,
	Tag,
	Alert,
	Button,
	Spin,
	Card,
} from "antd";
import {
	CheckCircleOutlined,
	ClockCircleOutlined,
	LoadingOutlined,
	ExclamationCircleOutlined,
	SyncOutlined,
	FileTextOutlined,
} from "@ant-design/icons";
import {
	fetchConsultJobStatus,
	cancelConsultJob,
} from "../../../api/healthcare/Consultation/ConsultApi";

const { Title, Text } = Typography;

interface ConsultProgressModalProps {
	visible: boolean;
	jobUuid: string | null;
	onComplete: (sessionId: number) => void;
	onCancel: () => void;
}

interface JobPart {
	topic_name: string;
	status: "pending" | "processing" | "completed" | "failed";
	progress_percent: number;
	error_message?: string;
	completed_at?: string;
}

interface JobStatus {
	status: "queued" | "processing" | "completed" | "failed" | "cancelled";
	progress: {
		total_steps: number;
		completed_steps: number;
		in_progress_steps: number;
		remaining_steps: number;
		progress_percentage: number;
		current_step: string | null;
	};
	job_uuid?: string;
	error_message?: string;
	started_at: string;
	completed_at?: string;
}

const ConsultProgressModal: React.FC<ConsultProgressModalProps> = ({
	visible,
	jobUuid,
	onComplete,
	onCancel,
}) => {
	const [jobStatus, setJobStatus] = useState<JobStatus>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const pollingInterval = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (visible && jobUuid) {
			startPolling();
		} else {
			stopPolling();
		}

		return () => stopPolling();
	}, [visible, jobUuid]);

	const startPolling = () => {
		fetchStatus();
		pollingInterval.current = setInterval(fetchStatus, 2000); // Poll every 2 seconds
	};

	const stopPolling = () => {
		if (pollingInterval.current) {
			clearInterval(pollingInterval.current);
			pollingInterval.current = null;
		}
	};

	const fetchStatus = async () => {
		if (!jobUuid) return;

		try {
			setLoading(true);
			setError(null);

			const response = await fetchConsultJobStatus(jobUuid);
			setJobStatus(response);

			// Stop polling if completed, failed, or cancelled
			if (
				response.status === "completed" ||
				response.status === "failed" ||
				response.status === "cancelled"
			) {
				stopPolling();

				// If completed, notify parent with session ID
				if (response.status === "completed" && response.session_id) {
					setTimeout(() => {
						onComplete(response.session_id);
					}, 1000);
				}
			}
		} catch (err) {
			console.error("Failed to fetch job status:", err);
			setError("Failed to fetch consultation generation status");
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = async () => {
		if (!jobUuid) return;

		try {
			await cancelConsultJob(jobUuid);
			stopPolling();
			onCancel();
		} catch (err) {
			console.error("Failed to cancel job:", err);
		}
	};

	const getStatusIcon = (status: string) => {
		switch (status) {
			case "completed":
				return <CheckCircleOutlined className="text-green-500" />;
			case "processing":
				return <LoadingOutlined className="text-blue-500" />;
			case "failed":
				return <ExclamationCircleOutlined className="text-red-500" />;
			default:
				return <ClockCircleOutlined className="text-gray-400" />;
		}
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case "completed":
				return "success";
			case "processing":
				return "processing";
			case "failed":
				return "error";
			case "queued":
				return "default";
			default:
				return "default";
		}
	};

	const canCancel =
		jobStatus?.status === "queued" || jobStatus?.status === "processing";

	return (
		<Modal
			title={
				<Space>
					<FileTextOutlined />
					<span>Generating Consultation Guidance</span>
				</Space>
			}
			open={visible}
			onCancel={canCancel ? handleCancel : onCancel}
			footer={[
				<Button
					key="cancel"
					onClick={canCancel ? handleCancel : onCancel}
					disabled={jobStatus?.status === "processing"}
				>
					{canCancel ? "Cancel" : "Close"}
				</Button>,
			]}
			width={700}
			closable={!canCancel}
			maskClosable={false}
		>
			{error && (
				<Alert
					message="Error"
					description={error}
					type="error"
					showIcon
					className="mb-4"
					action={
						<Button size="small" onClick={fetchStatus} icon={<SyncOutlined />}>
							Retry
						</Button>
					}
				/>
			)}

			{jobStatus && (
				<div>
					{/* Overall Progress */}
					<div className="mb-6">
						<div className="flex justify-between items-center mb-2">
							<Text strong>Overall Progress</Text>
							<Space>
								<Tag color={getStatusColor(jobStatus.status)}>
									{jobStatus.status.toUpperCase()}
								</Tag>
								<Text type="secondary">
									{jobStatus.progress.completed_steps} of {jobStatus.progress.total_steps} steps
								</Text>
							</Space>
						</div>
						<Progress
							percent={jobStatus.progress.progress_percentage}
							status={
								jobStatus.status === "failed"
									? "exception"
									: jobStatus.status === "completed"
									? "success"
									: "active"
							}
							strokeColor={{
								"0%": "#108ee9",
								"100%": "#87d068",
							}}
						/>
						{jobStatus.progress.current_step && (
							<Text type="secondary" className="text-sm">
								Currently processing: <strong>{jobStatus.progress.current_step}</strong>
							</Text>
						)}
					</div>

					{/* Error Message */}
					{jobStatus.error_message && (
						<Alert
							message="Generation Failed"
							description={jobStatus.error_message}
							type="error"
							showIcon
							className="mb-4"
						/>
					)}

					{/* Success Message */}
					{jobStatus.status === "completed" && (
						<Alert
							message="Consultation Guidance Generated Successfully"
							description="The consultation session is ready for review."
							type="success"
							showIcon
							className="mt-4"
						/>
					)}
				</div>
			)}

			{!jobStatus && loading && (
				<div className="text-center py-8">
					<Spin size="large" />
					<div className="mt-4">
						<Text type="secondary">Initializing consultation generation...</Text>
					</div>
				</div>
			)}
		</Modal>
	);
};

export default ConsultProgressModal;
